// src/api/expenses.ts
import { approvalService, type ExpenseDetails, type ApprovalRequestWithRelations } from '../services/approval'
import { authStore } from '../store/authStore'

// Re-export types for backward compatibility
export type ExpenseRequestWithApplicant = ApprovalRequestWithRelations & {
  type: 'expense'
}

// Helper to filter and cast expense requests
function toExpenseRequest(request: ApprovalRequestWithRelations): ExpenseRequestWithApplicant | null {
  if (request.type !== 'expense') return null
  return request as ExpenseRequestWithApplicant
}

export const expensesApi = {
  // Get all expense requests
  async getExpenseRequests(options?: {
    userId?: string
    status?: 'pending' | 'in_progress' | 'approved' | 'rejected'
    limit?: number
  }): Promise<ExpenseRequestWithApplicant[]> {
    const requests = await approvalService.getApprovalRequests({
      type: 'expense',
      status: options?.status,
      applicantId: options?.userId,
      limit: options?.limit
    })
    
    return requests
      .map(toExpenseRequest)
      .filter((r): r is ExpenseRequestWithApplicant => r !== null)
  },

  // Get single expense request
  async getExpenseRequestById(id: string): Promise<ExpenseRequestWithApplicant | null> {
    const request = await approvalService.getApprovalRequestById(id)
    if (!request) return null
    return toExpenseRequest(request)
  },

  // Create new expense request
  async createExpenseRequest(expense: {
    title: string
    expense_type: 'transport' | 'meal' | 'accommodation' | 'supplies' | 'other'
    amount: number
    currency: string
    description?: string
    receipt_url?: string
    expense_date: string
  }): Promise<ExpenseRequestWithApplicant> {
    const user = authStore.getState().user
    if (!user) throw new Error('User not authenticated')

    const details: ExpenseDetails = {
      expense_type: expense.expense_type,
      amount: expense.amount,
      currency: expense.currency,
      description: expense.description,
      receipt_url: expense.receipt_url,
      expense_date: expense.expense_date
    }

    const request = await approvalService.createExpenseRequest(
      user.id,
      expense.title,
      details
    )

    return request as ExpenseRequestWithApplicant
  },

  // Update expense request
  async updateExpenseRequest(id: string, updates: {
    title?: string
    status?: 'pending' | 'in_progress' | 'approved' | 'rejected'
    expense_type?: 'transport' | 'meal' | 'accommodation' | 'supplies' | 'other'
    amount?: number
    currency?: string
    description?: string
    receipt_url?: string
    expense_date?: string
  }): Promise<ExpenseRequestWithApplicant> {
    // Get current request to merge details
    const current = await approvalService.getApprovalRequestById(id)
    if (!current || current.type !== 'expense') {
      throw new Error('Expense request not found')
    }

    const updateData: Parameters<typeof approvalService.updateApprovalRequest>[1] = {}
    
    if (updates.title) updateData.title = updates.title
    if (updates.status) updateData.status = updates.status
    
    // If any detail fields are updated, reconstruct the details object
    if (updates.expense_type || updates.amount !== undefined || updates.currency || 
        updates.description !== undefined || updates.receipt_url !== undefined || 
        updates.expense_date) {
      const currentDetails = current.details as ExpenseDetails | null
      const newDetails: ExpenseDetails = {
        expense_type: updates.expense_type || currentDetails?.expense_type || 'other',
        amount: updates.amount !== undefined ? updates.amount : (currentDetails?.amount || 0),
        currency: updates.currency || currentDetails?.currency || 'KRW',
        description: updates.description !== undefined ? updates.description : currentDetails?.description,
        receipt_url: updates.receipt_url !== undefined ? updates.receipt_url : currentDetails?.receipt_url,
        expense_date: updates.expense_date || currentDetails?.expense_date || new Date().toISOString()
      }
      updateData.details = newDetails
    }

    const request = await approvalService.updateApprovalRequest(id, updateData)
    return request as ExpenseRequestWithApplicant
  },

  // Delete expense request
  async deleteExpenseRequest(id: string): Promise<void> {
    await approvalService.deleteApprovalRequest(id)
  },

  // Approve expense request
  async approveExpenseRequest(id: string): Promise<ExpenseRequestWithApplicant> {
    const user = authStore.getState().user
    if (!user) throw new Error('User not authenticated')
    
    const request = await approvalService.approveRequest(id, user.id)
    return request as ExpenseRequestWithApplicant
  },

  // Reject expense request
  async rejectExpenseRequest(id: string, reason: string): Promise<ExpenseRequestWithApplicant> {
    const user = authStore.getState().user
    if (!user) throw new Error('User not authenticated')
    
    const request = await approvalService.rejectRequest(id, user.id, reason)
    return request as ExpenseRequestWithApplicant
  },

  // Get expense statistics for a user
  async getExpenseStats(userId?: string): Promise<{
    totalAmount: number
    approvedAmount: number
    pendingAmount: number
    rejectedAmount: number
  }> {
    const targetUserId = userId || authStore.getState().user?.id
    if (!targetUserId) throw new Error('User ID required')
    
    return approvalService.getUserExpenseStats(targetUserId)
  },

  // Get pending expense approvals for current user
  async getPendingApprovals(): Promise<ExpenseRequestWithApplicant[]> {
    const user = authStore.getState().user
    if (!user) throw new Error('User not authenticated')
    
    const requests = await approvalService.getPendingApprovals(user.id)
    return requests
      .filter(r => r.type === 'expense')
      .map(r => r as ExpenseRequestWithApplicant)
  }
}

// Back-compat alias
export const getAll = expensesApi.getExpenseRequests
;(expensesApi as any).getAll = expensesApi.getExpenseRequests
